import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import { formatDateOnly } from '../utils/dateOnly';

/**
 * My Time Off (TO1b). The requester's own surface: file a request, see your
 * own history, withdraw while still pending. Gated on time_off:create, so a
 * driver — who holds create and nothing else (ruling 9) — gets this page and
 * no other time-off surface.
 *
 * Server 400s are shown VERBATIM. The routing refusals ("Mechanics has no
 * manager assigned yet") are the owner's data problem stated in the owner's
 * words; paraphrasing them here would hide which department is misconfigured.
 *
 * TO1c / ruling 11: blackouts WARN, they never block. The dates are checked
 * against the active blackouts as soon as both are picked, the overlap is
 * named inline before Submit is pressed, and the server repeats it in the
 * 201 response so the toast says it again. Submit is never disabled — the
 * approvers decide, and the server refuses nothing on a blackout.
 */

const STATUS_LABEL = {
  pending_manager: 'Awaiting manager',
  pending_gm: 'Awaiting GM',
  approved: 'Approved',
  denied: 'Denied',
  cancelled: 'Cancelled'
};
const STATUS_COLOR = {
  pending_manager: '#F59E0B',
  pending_gm: '#3B82F6',
  approved: '#10B981',
  denied: '#EF4444',
  cancelled: '#6B7280'
};

const MyTimeOff = () => {
  const [types, setTypes] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState('');
  const [blackouts, setBlackouts] = useState([]);
  const [form, setForm] = useState({
    time_off_type_id: '', start_date: '', end_date: '',
    start_half_day: false, end_half_day: false, reason: ''
  });

  // Ruling 11 pre-check. A driver holds create WITHOUT view, so GET
  // /blackouts answers 403 for them: the catch leaves the list empty and the
  // page behaves exactly as it did before TO1c.
  const clashes = (blackouts || []).filter(b =>
    form.start_date && form.end_date &&
    b.start_date <= form.end_date && b.end_date >= form.start_date);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [t, r, b] = await Promise.all([
        api.get('/time-off/types').then(res => res.data.types || []).catch(() => []),
        api.get('/time-off/requests').then(res => res.data.requests || []).catch(() => []),
        api.get('/time-off/blackouts').then(res => res.data.blackouts || []).catch(() => [])
      ]);
      setTypes(t);
      setRequests(r);
      setBlackouts(b);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const submit = async (e) => {
    e.preventDefault();
    setBanner('');
    if (!form.time_off_type_id || !form.start_date || !form.end_date) {
      setBanner('Pick a type and both dates.');
      return;
    }
    try {
      setSaving(true);
      const res = await api.post('/time-off/requests', {
        time_off_type_id: Number(form.time_off_type_id),
        start_date: form.start_date,
        end_date: form.end_date,
        start_half_day: form.start_half_day,
        end_half_day: form.end_half_day,
        reason: form.reason || null
      });
      // The SERVER's warning list, not the pre-check's: a blackout could have
      // been added between the page load and the press.
      const warnings = res?.data?.blackout_warnings || [];
      toast.success('Request submitted');
      warnings.forEach(w => toast.warning(
        `Heads up — ${formatDateOnly(w.start_date)} to ${formatDateOnly(w.end_date)} is a blackout period: ${w.reason}. Your request was still submitted.`
      ));
      setForm({ time_off_type_id: '', start_date: '', end_date: '', start_half_day: false, end_half_day: false, reason: '' });
      await fetchAll();
    } catch (err) {
      // Verbatim, both places: the toast for the glance, the banner to read.
      const msg = err.response?.data?.message || 'Could not submit the request';
      setBanner(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const withdraw = async (id) => {
    try {
      await api.delete(`/time-off/requests/${id}`);
      toast.success('Request withdrawn');
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not withdraw the request');
    }
  };

  const pending = (s) => s === 'pending_manager' || s === 'pending_gm';

  // Ledger item (v), closed in TO1c: a requester's own withdrawal and an
  // approver's cancellation share one status. On THIS page — the requester's
  // own surface — a self-cancel reads "Withdrawn", which is the word the
  // button used. Attendance keeps "Cancelled" and names the canceller.
  const statusLabel = (r) =>
    (r.status === 'cancelled' && r.cancelled_by && r.cancelled_by === r.user_id)
      ? 'Withdrawn'
      : (STATUS_LABEL[r.status] || r.status);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>My Time Off</h1>
        <p>Request time off and track where each request has got to.</p>
      </div>

      <div className="settings-card">
        <div className="card-header"><h3>New request</h3></div>
        <div className="card-body">
          {banner && (
            <div className="alert alert-error" style={{
              background: 'rgba(239,68,68,0.12)', border: '1px solid #EF4444',
              borderRadius: '8px', padding: '12px', marginBottom: '16px'
            }}>{banner}</div>
          )}
          <form onSubmit={submit}>
            <div className="form-group">
              <label>Type</label>
              <select
                value={form.time_off_type_id}
                onChange={e => setForm({ ...form, time_off_type_id: e.target.value })}
              >
                <option value="">— choose —</option>
                {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>First day</label>
                <input type="date" value={form.start_date}
                  onChange={e => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Last day</label>
                <input type="date" value={form.end_date}
                  onChange={e => setForm({ ...form, end_date: e.target.value })} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group checkbox-group">
                <label>
                  <input type="checkbox" checked={form.start_half_day}
                    onChange={e => setForm({ ...form, start_half_day: e.target.checked })} />
                  {' '}Half day on the first day
                </label>
              </div>
              <div className="form-group checkbox-group">
                <label>
                  <input type="checkbox" checked={form.end_half_day}
                    onChange={e => setForm({ ...form, end_half_day: e.target.checked })} />
                  {' '}Half day on the last day
                </label>
              </div>
            </div>
            <div className="form-group">
              <label>Reason (optional)</label>
              <textarea rows="2" value={form.reason}
                onChange={e => setForm({ ...form, reason: e.target.value })} />
            </div>
            {clashes.length > 0 && (
              <div className="alert alert-warning" style={{
                background: 'rgba(245,158,11,0.12)', border: '1px solid #F59E0B',
                borderRadius: '8px', padding: '12px', marginBottom: '16px', color: '#F5C36B'
              }}>
                <strong>These dates fall in a blackout period.</strong>
                {clashes.map((b, i) => (
                  <div key={i} style={{ marginTop: '6px' }}>
                    {formatDateOnly(b.start_date)} – {formatDateOnly(b.end_date)}: {b.reason}
                  </div>
                ))}
                <div style={{ marginTop: '6px', opacity: 0.85 }}>
                  You can still submit — your manager and the General Manager will see the same warning.
                </div>
              </div>
            )}
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Submitting…' : '📩 Submit request'}
            </button>
          </form>
        </div>
      </div>

      <div className="settings-card" style={{ marginTop: '20px' }}>
        <div className="card-header"><h3>My requests</h3></div>
        <div className="card-body">
          {loading ? <div className="loading">Loading…</div> : (
            <div className="permissions-table-wrapper">
              <table className="permissions-table">
                <thead>
                  <tr>
                    <th>Dates</th><th>Type</th><th>Hours</th><th>Status</th><th>Notes</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {requests.length === 0 && (
                    <tr><td colSpan="6">No requests yet.</td></tr>
                  )}
                  {requests.map(r => (
                    <tr key={r.id}>
                      <td>{r.start_date} → {r.end_date}</td>
                      <td>{r.type_name}</td>
                      <td>{r.hours_requested}</td>
                      <td>
                        <span style={{
                          background: STATUS_COLOR[r.status] || '#6B7280', color: '#fff',
                          borderRadius: '999px', padding: '2px 10px', fontSize: '12px'
                        }}>{statusLabel(r)}</span>
                      </td>
                      <td>{r.manager_review_notes || r.review_notes || r.cancel_notes || ''}</td>
                      <td>
                        {pending(r.status) && (
                          <button className="btn-secondary" onClick={() => withdraw(r.id)}>Withdraw</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyTimeOff;
